import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { useSelector, useStore } from 'react-redux'
import { formValueSelector, getFormValues, SubmissionError } from 'redux-form'
// components
import Header from 'components/UI/Modal/Header'
import { DrawerModal } from 'components/UI'
// view components
import CreatePlanForm from 'containers/PlanCreateForm'
// constants
import { ENTITIES } from 'constants/entities'
import { INITIAL_PER_PAGE } from 'constants/table'
import { Forms, Modals } from 'constants/index'
// graphql
import { PLAN_CREATE_MUTATION } from 'graphql/plan/mutations'
import { PLANS_LIST_QUERY } from 'graphql/plan/queries'
// hooks
import { useDefaultObjectSecurity, useModalData } from 'hooks'
// utils
import get from 'lodash/get'
import { runPendingUploads } from 'utils/pendingFileUploads'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getFieldValues, getOptionsByEntity, getValueFromOption, getValuesFromOptions } from 'utils/form'

const PAPER_PROPS = {
  style: {
    width: '600px',
    overflow: 'hidden'
  }
}

const PlanCreateModal = () => {
  const [planCreateMutation] = useMutation(PLAN_CREATE_MUTATION)

  const {
    modalData: { width, isOpen },
    hideModal
  } = useModalData({ modalType: Modals.PLAN_CREATE_MODAL })

  const router = useRouter()

  const { planType } = useSelector((state) => ({
    planType: formValueSelector(Forms.PLAN_CREATE_FORM)(state, 'planType')
  }))

  const defaultObjectSecurities = useDefaultObjectSecurity({
    objectApiName: 'plans',
    planTypeId: get(planType, 'value')
  })

  const store = useStore()

  const handleFormSubmit = async (data, _, { fields }) => {
    try {
      await runPendingUploads(Forms.PLAN_CREATE_FORM)

      const formValues = getFormValues(Forms.PLAN_CREATE_FORM)(store.getState()) || data
      data = formValues

      const filter = {}
      const slug = router.query.slug?.[0]

      if (slug) {
        filter.filterSlug = slug
      }

      const {
        data: {
          planCreate: {
            data: { id, companyId }
          }
        }
      } = await planCreateMutation({
        variables: {
          input: {
            fieldValues: getFieldValues(fields, data, defaultObjectSecurities),
            memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
            memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
            name: data.planTitle,
            ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
            planTypeId: getValueFromOption(data.planType),
            status: getValueFromOption(data.status)
          }
        },
        // TODO: check
        refetchQueries: [
          {
            query: PLANS_LIST_QUERY,
            variables: {
              filter,
              pagination: {
                pageSkip: 0,
                pageTake: INITIAL_PER_PAGE
              }
            }
          }
        ]
      })

      router.push({
        pathname: `/plans/${companyId}/edit/${id}`
      })

      hideModal()
    } catch (errors) {
      console.log('[CreatePlanForm.handleFormSubmit]: ', parseGraphQLErrorMessages(errors))
      throw new SubmissionError({ _error: parseGraphQLErrorMessages(errors) })
    }
  }

  return (
    <DrawerModal width={width} isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header handleClearClick={hideModal}>Create Plan</Header>
      <CreatePlanForm hideModal={hideModal} form={Forms.PLAN_CREATE_FORM} onSubmit={handleFormSubmit} />
    </DrawerModal>
  )
}

export default PlanCreateModal
